import React, { useState, useMemo } from 'react';
import * as XLSX from 'xlsx'; 
import { db } from '../../../../firebase/config';
import { collection, getDocs, collectionGroup, addDoc, writeBatch, doc } from 'firebase/firestore';
// Importamos los helpers compartidos que normalizan importes, pólizas y periodos
import { formatCurrency, parseImporteExcel, normalizePoliza, normalizeText, normalizePeriodo } from './helpers';

/**
 * Helper de normalización local de strings para cruce seguro de canales y subcanales
 */
const safeNormalize = (str) => {
    if (!str) return '';
    return String(str)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
};

/**
 * cleanPayload - Función de limpieza recursiva defensiva.
 * Elimina de forma atómica del objeto cualquier propiedad que contenga strings vacíos,
 * valores nulos o indefinidos antes de persistir en Firebase.
 */
const cleanPayload = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    const cleaned = {};
    Object.entries(obj).forEach(([key, val]) => {
        if (val === undefined || val === null || val === '') {
            return; // Filtramos el campo omitiéndolo
        }
        if (typeof val === 'object' && !Array.isArray(val)) {
            cleaned[key] = cleanPayload(val);
        } else {
            cleaned[key] = val;
        }
    });
    return cleaned;
};

/**
 * ImportadorContratosTab - Componente especializado en liquidaciones de ART.
 * Realiza un cruce masivo mediante collectionGroup con la subcolección 'contratos'
 * e inyecta un snapshot transaccional inmutable con resguardo total de IDs de Firebase.
 */
const ImportadorContratosTab = ({ maestros, setActiveTab }) => {
    const [loading, setLoading] = useState(false);
    const [previewData, setPreviewData] = useState([]);
    const [stats, setStats] = useState({ total: 0, ok: 0, error: 0 });
    const [fileName, setFileName] = useState('');
    const [localError, setLocalError] = useState(null); // Reemplazo premium para alert()

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLocalError(null);
            setFileName(file.name);
            setPreviewData([]);
            setStats({ total: 0, ok: 0, error: 0 });
            // Guardamos el archivo en un objeto global temporal para el procesamiento
            window.tempSelectedFile = file; 
        }
    };

    const handleProcessFile = async () => {
        const file = window.tempSelectedFile;
        if (!file) {
            setLocalError("Por favor, selecciona un archivo primero antes de iniciar el análisis.");
            return;
        }
        setLoading(true);
        setLocalError(null);

        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws, { header: 1 }); 
                await processExcelRows(data);
            } catch (error) {
                console.error("Error al leer el archivo Excel:", error);
                setLocalError("Hubo un error al procesar el archivo Excel. Por favor, verifica que no esté protegido o dañado.");
                setLoading(false);
            }
        };
        reader.readAsBinaryString(file);
    };

    const processExcelRows = async (rows) => {
        if (rows.length < 2) { 
            setLoading(false); 
            setLocalError("El archivo seleccionado está vacío o no contiene suficientes filas para el análisis.");
            return; 
        }

        // 1. Identificación de cabeceras de forma flexible con blindaje para celdas vacías/nulas
        let headerRowIndex = -1;
        for (let i = 0; i < Math.min(rows.length, 20); i++) {
            if (!rows[i]) continue;
            const rowStr = rows[i].map(c => c !== undefined && c !== null ? normalizeText(c) : '');
            // Buscamos columnas esenciales: Contrato/Póliza y Comisiones/Primas
            if ((rowStr.some(h => h.includes('contrato') || h.includes('poliza') || h.includes('nro'))) && 
               (rowStr.some(h => h.includes('comision') || h.includes('prima') || h.includes('importe')))) {
                headerRowIndex = i;
                break;
            }
        }

        if (headerRowIndex === -1) { 
            setLoading(false); 
            setLocalError("No se pudieron detectar las columnas de control necesarias (Contrato/Póliza e Importes) en las primeras filas del documento.");
            return; 
        }

        const header = rows[headerRowIndex].map(h => String(h || '').toLowerCase().trim());
        const idxPeriodo = header.findIndex(h => h.includes('periodo') || h.includes('fecha'));
        const idxContrato = header.findIndex(h => h.includes('contrato') || h.includes('poliza') || h.includes('nro'));
        const idxComision = header.findIndex(h => h.includes('comision') || h.includes('importe'));
        const idxPrima = header.findIndex(h => h.includes('prima') || h.includes('premio'));

        // 2. Mapeo Maestro de Contratos (Cruzamiento de datos en una pasada)
        const contratosRef = collectionGroup(db, 'contratos');
        const snapshot = await getDocs(contratosRef);
        const contratosMap = new Map();
        
        snapshot.forEach(docSnap => {
            const data = docSnap.data();
            if (data.nContrato) {
                const normalizedNum = normalizePoliza(data.nContrato);
                contratosMap.set(normalizedNum, { 
                    id: docSnap.id, 
                    clienteId: docSnap.ref.path.split('/')[3], // ID del cliente desde el path jerárquico
                    ...data 
                });
            }
        });

        const processed = [];
        let countOk = 0;
        let countError = 0;

        // 3. Procesamiento fila por fila
        for (let i = headerRowIndex + 1; i < rows.length; i++) {
            const row = rows[i];
            const rawContrato = row[idxContrato];
            if (!rawContrato) continue; 

            const nContratoExcel = String(rawContrato).trim(); 
            const nContratoNorm = normalizePoliza(nContratoExcel);
            
            const valComision = idxComision !== -1 ? parseImporteExcel(row[idxComision]) : 0;
            const valPrima = idxPrima !== -1 ? parseImporteExcel(row[idxPrima]) : 0;
            
            const periodoStd = normalizePeriodo(idxPeriodo !== -1 ? row[idxPeriodo] : '');

            const match = contratosMap.get(nContratoNorm);
            let status = 'ERROR';
            let message = 'Contrato no encontrado en el sistema';
            let dataToSave = null;

            if (match) {
                if (valComision !== 0 || valPrima !== 0) {
                    status = 'OK';
                    message = 'Registro válido para importar';
                    countOk++;

                    // --- RESOLUTOR DE MAESTROS RELACIONALES (Recuperación de IDs Faltantes) ---
                    const canalNorm = safeNormalize(match.canalNombre || match.subcanal || '');
                    const subcanalNorm = safeNormalize(match.subcanalNombre || '');

                    const canalMaestro = maestros?.mapaCanales?.[match.canalNombreId] || maestros?.mapaCanales?.[canalNorm];
                    const subcanalMaestro = maestros?.mapaSubcanales?.[match.subcanalNombreId] || maestros?.mapaSubcanalesNombre?.[subcanalNorm];

                    const formaLiq = canalMaestro?.formaLiquidarART || 'Comisión';
                    
                    // Sincronización curada de ID de Aseguradora
                    let resolvedCompaniaId = match.companiaId || '';
                    if (!resolvedCompaniaId && match.compania && maestros?.companias) {
                        const foundCia = maestros.companias.find(c => 
                            safeNormalize(c.nombre) === safeNormalize(match.compania)
                        );
                        if (foundCia) resolvedCompaniaId = foundCia.id;
                    }

                    // Sincronización curada de ID de Ramo (ART)
                    let resolvedRiesgoId = match.riesgoId || '';
                    if (!resolvedRiesgoId && maestros?.riesgos) {
                        const foundRiesgo = maestros.riesgos.find(r => 
                            safeNormalize(r.nombre) === 'art' || 
                            safeNormalize(r.nombre).includes('riesgos del trabajo') ||
                            safeNormalize(r.nombre).includes('riesgos de trabajo')
                        );
                        if (foundRiesgo) resolvedRiesgoId = foundRiesgo.id;
                    }

                    // Saneamos la denormalización garantizando todos los IDs en el documento de liquidación
                    dataToSave = {
                        nContrato: match.nContrato || '',
                        contratoId: match.id,
                        clienteId: match.clienteId || '',
                        clienteNombre: match.clienteNombre || match.clienteNombreCompleto || 'S/D',
                        clienteApellido: match.clienteApellido || '',
                        clienteCuit: match.clienteCuit || match.cuit || '',
                        clienteDni: match.clienteDni || match.dni || '',
                        compania: match.compania || '',
                        companiaId: resolvedCompaniaId, 
                        riesgo: match.riesgo || 'ART',
                        riesgoId: resolvedRiesgoId,     
                        
                        // Canales y Asociados con IDs Saneados síncronamente
                        canal: match.canal || canalMaestro?.canal || '',
                        canalNombre: canalMaestro?.nombreDisplay || match.canalNombre || '',
                        canalNombreId: canalMaestro?.id || match.canalNombreId || '',
                        subcanalNombre: subcanalMaestro?.nombre || match.subcanalNombre || '',
                        subcanalNombreId: subcanalMaestro?.id || match.subcanalNombreId || '',
                        
                        importeComision: valComision,
                        importePrima: valPrima,
                        formaLiquidacion: formaLiq,
                        type: 'contratos', // Tipo de colección operativa
                        
                        // Snapshot comercial histórico de incentivos
                        comisAsociado: parseFloat(match.comisionAsociado) || 0,
                        comisOrganizador: parseFloat(match.comisionOrganizador) || 0,
                        descAsociado: parseFloat(match.descuentoAsociado) || 0,
                        descOrganizador: parseFloat(match.descuentoOrganizador) || 0
                    };
                } else {
                    message = 'Importes vacíos o en cero';
                    countError++;
                }
            } else {
                countError++;
            }

            processed.push({
                id: i,
                periodo: periodoStd,
                nContrato_Excel: nContratoExcel, 
                valComision,
                valPrima,
                status,
                message,
                dataToSave
            });
        }

        setPreviewData(processed);
        setStats({ total: processed.length, ok: countOk, error: countError });
        setLoading(false);
    };

    const handleConfirmImport = async () => {
        const toImport = previewData.filter(i => i.status === 'OK');
        if (toImport.length === 0) return;
        setLoading(true);
        setLocalError(null);

        try {
            const batchId = new Date().getTime().toString();
            
            // 1. Crear el registro maestro del lote (Remesa)
            const remesaRef = await addDoc(collection(db, 'public/data/remesas'), {
                id: batchId,
                fecha: new Date().toISOString(),
                nombreArchivo: fileName,
                registros: toImport.length,
                periodo: toImport[0].periodo,
                tipo: 'ART'
            });
            const remesaDocId = remesaRef.id;

            // 2. Guardar cada liquidación individual aplicando sanitización síncrona
            const batch = writeBatch(db);
            
            toImport.forEach((item) => {
                const cleanData = cleanPayload(item.dataToSave);
                const liqRef = doc(collection(db, 'public/data/liquidaciones'));
                
                batch.set(liqRef, {
                    periodo: item.periodo,
                    nContrato: item.dataToSave.nContrato,
                    fechaImportacion: new Date().toISOString(),
                    batchId: batchId,
                    remesaDocId: remesaDocId, 
                    ...cleanData 
                });
            });

            await batch.commit();

            setPreviewData([]);
            setFileName('');
            window.tempSelectedFile = null;
            setActiveTab('remesas'); // Navegamos al historial al finalizar

        } catch (error) { 
            console.error("Error al guardar en Firebase:", error); 
            setLocalError("No se pudieron persistir los registros debido a un error en el servidor. Por favor, reintente en unos minutos.");
        } finally { 
            setLoading(false); 
        }
    };

    return (
        <div className="bg-white p-8 rounded-[2rem] shadow-xl border border-gray-100 animate-fade-in text-left">
            
            {localError && (
                <div className="mb-6 p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 flex items-start gap-3.5 animate-fade-in">
                    <svg className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="text-xs font-bold leading-normal">{localError}</div>
                </div>
            )}

            <div className="mb-10 border-b border-gray-100 pb-10">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter">Importador de Liquidaciones ART</h3>
                        <p className="text-xs text-slate-400 font-bold uppercase mt-1 tracking-widest italic">Cruce masivo por número de contrato</p>
                    </div>
                    {fileName && (
                        <div className="flex items-center gap-2 bg-orange-50 px-4 py-2 rounded-2xl border border-orange-100 shadow-sm">
                            <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                            <span className="text-[10px] font-black text-orange-700 uppercase tracking-widest">{fileName}</span>
                        </div>
                    )}
                </div>
                
                <div className="flex flex-col gap-6 items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-slate-200 border-dashed rounded-[2.5rem] cursor-pointer bg-slate-50/50 hover:bg-orange-50 hover:border-orange-300 transition-all group overflow-hidden relative">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6 z-10">
                            <div className="bg-white p-4 rounded-3xl shadow-md mb-4 group-hover:scale-110 transition-transform duration-300">
                                <svg className="w-10 h-10 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2a2 2 0 00-2-2H5a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                            <p className="mb-2 text-base text-slate-600 font-bold uppercase tracking-tighter">Suelte el archivo aquí o <span className="text-orange-600">busque en su PC</span></p>
                            <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em]">Formato admitido: XLSX, XLS, CSV</p>
                        </div>
                        <input type="file" className="hidden" accept=".xlsx, .xls, .csv" onChange={handleFileSelect} />
                    </label>

                    <button 
                        onClick={handleProcessFile} 
                        disabled={!fileName || loading} 
                        className={`px-12 py-4 rounded-2xl font-black uppercase text-xs tracking-[0.2em] shadow-2xl transition-all transform active:scale-95 ${!fileName || loading ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none' : 'bg-slate-900 text-white hover:bg-slate-800 shadow-slate-200'}`}
                    >
                        {loading ? (
                            <div className="flex items-center gap-3">
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                Analizando Filas...
                            </div>
                        ) : 'Iniciar Análisis de ART'}
                    </button>
                </div>
            </div>

            {/* TABLA DE VISTA PREVIA */}
            {!loading && previewData.length > 0 && (
                <div className="animate-fade-in text-left">
                    <div className="flex justify-between items-center mb-6 bg-slate-900 p-5 rounded-[1.5rem] shadow-xl text-left">
                        <h3 className="text-xs font-black text-white uppercase tracking-[0.2em]">Resumen de validación</h3>
                        <div className="flex gap-4">
                            <div className="bg-white/10 px-4 py-2 rounded-xl text-center"><p className="text-[9px] font-black text-slate-400 uppercase">Procesados</p><p className="text-lg font-black text-white leading-none mt-1">{stats.total}</p></div>
                            <div className="bg-emerald-500/20 px-4 py-2 rounded-xl text-center"><p className="text-[9px] font-black text-emerald-400 uppercase">Correctos</p><p className="text-lg font-black text-emerald-400 leading-none mt-1">{stats.ok}</p></div>
                            <div className="bg-rose-500/20 px-4 py-2 rounded-xl text-center"><p className="text-[9px] font-black text-rose-400 uppercase">Errores</p><p className="text-lg font-black text-rose-400 leading-none mt-1">{stats.error}</p></div>
                        </div>
                    </div>

                    <div className="overflow-x-auto border border-slate-100 rounded-[1.5rem] mb-8 shadow-inner bg-slate-50/50 max-h-[500px] custom-scrollbar">
                        <table className="min-w-full text-left border-separate border-spacing-0">
                            <thead className="sticky top-0 bg-white shadow-sm z-10 text-left">
                                <tr className="text-slate-400 font-black uppercase text-[10px] tracking-widest">
                                    <th className="px-8 py-5 border-b border-slate-100">Contrato/Póliza</th>
                                    <th className="px-8 py-5 border-b border-slate-100">Periodo</th>
                                    <th className="px-8 py-5 border-b border-slate-100 text-right">Comisión $</th>
                                    <th className="px-8 py-5 border-b border-slate-100 text-right">Prima $</th>
                                    <th className="px-8 py-5 border-b border-slate-100 text-center">Estado de Fila</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {previewData.map((r, i) => (
                                    <tr key={i} className={`group transition-all ${r.status === 'OK' ? 'hover:bg-emerald-50/50' : 'bg-rose-50/30 hover:bg-rose-50'}`}>
                                        <td className="px-8 py-4 font-mono font-black text-slate-600 border-b border-slate-50 text-sm">{r.nContrato_Excel}</td>
                                        <td className="px-8 py-4 text-slate-500 font-bold border-b border-slate-50">{r.periodo}</td>
                                        <td className="px-8 py-4 font-black text-slate-700 border-b border-slate-50 text-right">{formatCurrency(r.valComision)}</td>
                                        <td className="px-8 py-4 font-black text-slate-700 border-b border-slate-50 text-right">{formatCurrency(r.valPrima)}</td>
                                        <td className="px-8 py-4 border-b border-slate-50 text-center">
                                            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-tighter border ${r.status === 'OK' ? 'text-emerald-600 bg-emerald-50 border-emerald-100' : 'text-rose-600 bg-rose-50 border-rose-100'}`}>
                                                {r.status === 'OK' ? '✓' : '✕'} {r.message}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-end gap-6 p-4 items-center">
                        <button 
                            onClick={() => { setPreviewData([]); setFileName(''); window.tempSelectedFile = null; }} 
                            className="text-slate-400 hover:text-rose-500 font-black uppercase text-[11px] tracking-widest transition-colors"
                        >
                            Cancelar Importación
                        </button>
                        <button 
                            onClick={handleConfirmImport} 
                            disabled={stats.ok === 0} 
                            className="px-12 py-3.5 rounded-2xl bg-emerald-600 text-white font-black uppercase text-[11px] tracking-[0.2em] shadow-xl shadow-emerald-200 hover:bg-emerald-700 transition-all transform active:scale-95 disabled:grayscale disabled:shadow-none"
                        >
                            Confirmar Lote de ART
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            `}</style>
        </div>
    );
};

export default ImportadorContratosTab;